import { Component, computed, input, signal } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Card } from './card';
import { InfoTip } from './info-tip';
import { formatAge } from './format-age';
import { AppHealth, HealthStatus, RequestLogEntry, STATUS_LABELS } from './models';
import { portalResourceUrl } from './portal-link';
import { healthTone } from './tone';

const STATUS_HELP: Record<HealthStatus, string> = {
  Healthy: 'Working normally: very few failed requests or calls, and few recent exceptions.',
  Degraded:
    'Working, but with a noticeable number of failures or exceptions. Worth keeping an eye on.',
  Unhealthy:
    'Many requests or calls are failing, or there are many recent exceptions. This probably needs attention.',
  NoData:
    'No requests or outgoing calls were recorded recently, so there is nothing to judge. Background workers often look like this.',
  Error: 'The dashboard could not read this application’s data from Application Insights.',
  AccessDenied:
    'The dashboard is not allowed to read this application’s data. This says nothing about its health.',
};

const OUTCOME_LABELS: Record<RequestLogEntry['outcome'], string> = {
  Success: 'OK',
  ClientError: 'Client error',
  ServerError: 'Server error',
};

@Component({
  selector: 'app-health-card',
  imports: [Card, DatePipe, DecimalPipe, InfoTip],
  templateUrl: './health-card.html',
  styleUrl: './health-card.css',
})
export class HealthCard {
  readonly app = input.required<AppHealth>();
  // The page's clock and the age after which a reading counts as old; the page owns both so every card agrees.
  readonly now = input(new Date());
  readonly staleAfterMs = input(Infinity);

  protected readonly age = computed(() => formatAge(this.app().checkedAt, this.now()));
  protected readonly stale = computed(
    () => this.now().getTime() - new Date(this.app().checkedAt).getTime() > this.staleAfterMs(),
  );

  protected readonly roleHelp =
    'A role is one running part of an application, such as its API or a background worker. Several roles can share one Application Insights resource, and each is judged separately.';
  protected readonly clientErrorHelp =
    'Requests that failed because the caller sent something wrong, such as a missing login (4xx). They are shown here but do not lower the status.';

  protected readonly statusLabel = computed(() => STATUS_LABELS[this.app().status]);
  protected readonly statusHelp = computed(() => STATUS_HELP[this.app().status]);
  protected readonly exceptionsHelp = computed(
    () =>
      `Errors thrown inside the app in the last ${this.app().exceptionWindowMinutes} minutes. Many of them lower the status, even when requests look fine.`,
  );
  protected readonly tone = computed(() => healthTone(this.app().status));

  // Null when the resource cannot be addressed: an app known only by its ID has no name, group or subscription.
  protected readonly portalUrl = computed(() => {
    const { name, resourceGroup, subscriptionId } = this.app();
    return name && resourceGroup && subscriptionId
      ? portalResourceUrl(subscriptionId, resourceGroup, ['Microsoft.Insights', 'components', name])
      : null;
  });

  protected readonly copied = signal(false);

  // Failed calls first, so what is broken is at the top; client errors next, then successes.
  private static readonly outcomeRank: Record<RequestLogEntry['outcome'], number> = {
    ServerError: 0,
    ClientError: 1,
    Success: 2,
  };

  // The rows are only built once the list is opened: a card can hold 200 of them.
  protected readonly detailsOpen = signal(false);
  private readonly failuresOnlyChoice = signal<boolean | null>(null);

  protected readonly failedCount = computed(
    () => this.app().latestRequests.filter((request) => request.outcome === 'ServerError').length,
  );
  // Failures only is the default whenever something failed; the reader can switch it off.
  protected readonly onlyFailures = computed(
    () => this.failedCount() > 0 && (this.failuresOnlyChoice() ?? true),
  );
  protected readonly visibleRequests = computed(() => {
    const requests = [...this.app().latestRequests].sort(
      (a, b) => HealthCard.outcomeRank[a.outcome] - HealthCard.outcomeRank[b.outcome],
    );
    return this.onlyFailures()
      ? requests.filter((request) => request.outcome === 'ServerError')
      : requests;
  });

  // The resource ID is only needed now and then, so it is copied rather than always shown.
  protected async copyId(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.app().appId);
    } catch {
      return;
    }
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 2000);
  }

  protected onToggle(event: Event): void {
    this.detailsOpen.set((event.target as HTMLDetailsElement).open);
  }

  protected setOnlyFailures(event: Event): void {
    this.failuresOnlyChoice.set((event.target as HTMLInputElement).checked);
  }

  // "Received" or "Called HTTP": in plain words, whether the app was asked or did the asking.
  protected kindLabel(request: RequestLogEntry): string {
    return request.direction === 'Incoming'
      ? 'Received'
      : `Called ${request.channel.replace(/ calls$/, '')}`;
  }

  // Splitting after each slash lets a long path wrap at a segment boundary.
  protected nameParts(name: string): string[] {
    return name.split(/(?<=\/)/);
  }

  // A failed outgoing call is not a server error of this app, so it is labelled plainly.
  protected outcomeLabel(request: RequestLogEntry): string {
    return request.direction === 'Outgoing' && request.outcome === 'ServerError'
      ? 'Failed'
      : OUTCOME_LABELS[request.outcome];
  }
}
